import { compact, flatten, groupBy, uniq } from "lodash";

import { type TopLevelBreakdown } from "@features/sales-v3/types";
import {
  type SalesTableRow,
  type SalesTableVisualRow,
} from "@features/sales-v3/types/table";
import { type GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual_rows } from "@north-beam/nb-client-side";
import {
  type MetricPoint,
  addToMetricPoint,
  newMetricPoint,
} from "@north-beam/nb-common/metrics";

interface ForecastPoint {
  forecast_rev__30: number;
  forecast_rev__60: number;
  forecast_rev__90: number;
  forecast_txns__30: number;
  forecast_txns__60: number;
  forecast_txns__90: number;
}

export function recursivelySearchForRows(rows: SalesTableRow[]) {
  return flatten(
    compact(
      rows.map((row) => {
        if (row.rowType === "normal") {
          const flatRowIds: SalesTableVisualRow[] = [row];
          if (row.children?.length) {
            flatRowIds.push(...recursivelySearchForRows(row.children));
          }
          return flatRowIds;
        }
        return null;
      }),
    ),
  );
}

function newForecastPoint() {
  return {
    forecast_rev__30: 0,
    forecast_rev__60: 0,
    forecast_rev__90: 0,
    forecast_txns__30: 0,
    forecast_txns__60: 0,
    forecast_txns__90: 0,
  };
}

function addForecastToPoint(target: ForecastPoint, source: ForecastPoint) {
  return {
    forecast_rev__30: target.forecast_rev__30 + source.forecast_rev__30,
    forecast_rev__60: target.forecast_rev__60 + source.forecast_rev__60,
    forecast_rev__90: target.forecast_rev__90 + source.forecast_rev__90,
    forecast_txns__30: target.forecast_txns__30 + source.forecast_txns__30,
    forecast_txns__60: target.forecast_txns__60 + source.forecast_txns__60,
    forecast_txns__90: target.forecast_txns__90 + source.forecast_txns__90,
  };
}

export function processPreloadedData(
  breakdowns: TopLevelBreakdown[],
  rows: GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual_rows[],
) {
  const result: {
    breakdowns: TopLevelBreakdown[];
    rows: GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual_rows[];
  }[] = [];

  for (let i = 1; i < breakdowns.length; i++) {
    const path = breakdowns.slice(0, i);
    const processedRows = [];

    const grouped = groupBy(rows, (row) =>
      JSON.stringify(path.map((p) => ({ [p.key]: row.dimensions[p.key] }))),
    );

    for (const [key, values] of Object.entries(grouped)) {
      const groups = JSON.parse(key);
      const dimensions: Record<string, string> = {};

      groups.forEach((group: Record<string, string>) => {
        Object.entries(group).forEach(
          ([key, value]) => (dimensions[key] = value),
        );
      });

      const metricsByDate: Record<string, MetricPoint> = {};
      const forecastByDate: Record<string, ForecastPoint> = {};

      values.forEach((value) => {
        if (value.dailyMetrics) {
          value.dailyMetrics.forEach((metric) => {
            let metricPoint = metricsByDate[metric.date];
            if (!metricPoint) {
              metricPoint = newMetricPoint();
            }

            addToMetricPoint(metricPoint, metric);
            metricsByDate[metric.date] = metricPoint;
          });
        }

        if (value.forecastV1Daily) {
          value.forecastV1Daily.forEach((forecast) => {
            let forecastPoint = forecastByDate[forecast.date];
            if (!forecastPoint) {
              forecastPoint = newForecastPoint();
            }

            forecastByDate[forecast.date] = addForecastToPoint(
              forecastPoint,
              forecast,
            );
          });
        }
      });

      processedRows.push({
        __typename: "SalesTableReportV3Row",
        dimensions,
        deepestAtomicityCounts: values.reduce(
          (acc, e) => (acc += e.deepestAtomicityCounts),
          0,
        ),
        dailyMetrics: Object.entries(metricsByDate).map(([date, metrics]) => ({
          date,
          ...metrics,
        })),
        name: Object.values(dimensions).join(" / "),
        statuses: uniq(values.flatMap((value) => value.statuses)).sort(),
        adName: null,
        adsetName: null,
        campaignName: null,
        imageUri: null,
        objectId: `{"nbt":${key}`,
        link: "",
        nbtPlatformID: "",
        cellAnnotations: {},
        forecastV1Daily: Object.entries(forecastByDate).map(
          ([date, forecast]) => ({
            groupingKey: key,
            date,
            ...forecast,
          }),
        ),
        key,
      } as GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual_rows);
    }

    result.push({ breakdowns: path, rows: processedRows });
  }

  result.push({ breakdowns, rows });

  return result;
}
